import React, { useState, createElement } from 'react';
import { SearchIcon, FilterIcon, XIcon, ChevronRightIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, PaperclipIcon, TagIcon } from 'lucide-react';
import { Note } from './NotesManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'timestamp' | 'module' | 'noteType' | 'author' | 'visibility';
type SortDirection = 'asc' | 'desc';
interface NotesTableProps {
  notes: Note[];
  onViewNote: (note: Note) => void;
  locale: string;
}
export function NotesTable({
  notes,
  onViewNote,
  locale
}: NotesTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    moduleType: '',
    noteType: '',
    author: '',
    visibility: '',
    entity: '',
    site: '',
    dateFrom: '',
    dateTo: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      moduleType: '',
      noteType: '',
      author: '',
      visibility: '',
      entity: '',
      site: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
  };
  // Get unique values for filter dropdowns
  const uniqueModuleTypes = Array.from(new Set(notes.map(note => note.moduleType)));
  const uniqueNoteTypes = Array.from(new Set(notes.map(note => note.noteTypeName)));
  const uniqueAuthors = Array.from(new Set(notes.map(note => note.authorName)));
  const uniqueVisibilities = Array.from(new Set(notes.map(note => note.visibility)));
  const uniqueEntities = Array.from(new Set(notes.filter(note => note.entityName).map(note => note.entityName)));
  const uniqueSites = Array.from(new Set(notes.filter(note => note.siteName).map(note => note.siteName)));
  // Apply filters and search
  const filteredNotes = notes.filter(note => {
    // Search term filter
    const searchMatch = searchTerm === '' || note.body.toLowerCase().includes(searchTerm.toLowerCase()) || note.tags && note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) || note.moduleId.toLowerCase().includes(searchTerm.toLowerCase()) || note.moduleName.toLowerCase().includes(searchTerm.toLowerCase());
    // Module type filter
    const moduleTypeMatch = !filters.moduleType || note.moduleType === filters.moduleType;
    // Note type filter
    const noteTypeMatch = !filters.noteType || note.noteTypeName === filters.noteType;
    // Author filter
    const authorMatch = !filters.author || note.authorName === filters.author;
    // Visibility filter
    const visibilityMatch = !filters.visibility || note.visibility === filters.visibility;
    // Entity filter
    const entityMatch = !filters.entity || note.entityName === filters.entity;
    // Site filter
    const siteMatch = !filters.site || note.siteName === filters.site;
    // Date range filter
    const noteDate = new Date(note.createdAt);
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    const dateMatch = (!fromDate || noteDate >= fromDate) && (!toDate || noteDate <= toDate);
    return searchMatch && moduleTypeMatch && noteTypeMatch && authorMatch && visibilityMatch && entityMatch && siteMatch && dateMatch;
  });
  // Apply sorting
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'timestamp':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'module':
        const moduleCompare = a.moduleType.localeCompare(b.moduleType);
        if (moduleCompare !== 0) {
          comparison = moduleCompare;
        } else {
          comparison = a.moduleId.localeCompare(b.moduleId);
        }
        break;
      case 'noteType':
        comparison = a.noteTypeName.localeCompare(b.noteTypeName);
        break;
      case 'author':
        comparison = a.authorName.localeCompare(b.authorName);
        break;
      case 'visibility':
        comparison = a.visibility.localeCompare(b.visibility);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Function to export notes to CSV
  const exportToCSV = () => {
    // Header row
    let csvContent = 'Module Type,Module ID,Module Name,Note Type,Visibility,Author,Created At,Body,Tags\n';
    // Data rows
    sortedNotes.forEach(note => {
      const row = [note.moduleType, note.moduleId, note.moduleName, note.noteTypeName, note.visibility, note.authorName, new Date(note.createdAt).toLocaleString(locale), `"${note.body.replace(/"/g, '""')}"`, note.tags ? note.tags.join(', ') : ''];
      csvContent += row.join(',') + '\n';
    });
    // Create and download the file
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `notes_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Truncate body text for display
  const truncateBody = (body: string, maxLength = 100) => {
    if (body.length <= maxLength) return body;
    return body.substring(0, maxLength) + '...';
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'Public':
        return <StatusBadge status="success" text="Public" data-id="element-19193" />;
      case 'Role-based':
        return <StatusBadge status="warning" text="Role-based" data-id="element-19194" />;
      case 'User-specific':
        return <StatusBadge status="info" text="User-specific" data-id="element-19195" />;
      case 'Creator-only':
        return <StatusBadge status="error" text="Private" data-id="element-19196" />;
      default:
        return <StatusBadge status="default" text={visibility} data-id="element-19197" />;
    }
  };
  return <div className="space-y-6" data-id="element-19198">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-19199">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-19200">
          <div className="flex-1 max-w-md" data-id="element-19201">
            <div className="relative rounded-md shadow-sm" data-id="element-19202">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19203">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-19204" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search in note body or tags..." data-id="element-19205" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-19206">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19207">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-19208" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-19209">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={exportToCSV} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19210">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-19211" />
              Export CSV
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-19212">
            {filters.moduleType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-19213">
                Module: {filters.moduleType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            moduleType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-19214">
                  <XIcon className="h-3 w-3" data-id="element-19215" />
                </button>
              </span>}
            {filters.noteType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-19216">
                Type: {filters.noteType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            noteType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-19217">
                  <XIcon className="h-3 w-3" data-id="element-19218" />
                </button>
              </span>}
            {filters.author && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-19219">
                Author: {filters.author}
                <button type="button" onClick={() => setFilters({
            ...filters,
            author: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-19220">
                  <XIcon className="h-3 w-3" data-id="element-19221" />
                </button>
              </span>}
            {filters.visibility && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-19222">
                Visibility: {filters.visibility}
                <button type="button" onClick={() => setFilters({
            ...filters,
            visibility: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-19223">
                  <XIcon className="h-3 w-3" data-id="element-19224" />
                </button>
              </span>}
            {filters.entity && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-19225">
                Entity: {filters.entity}
                <button type="button" onClick={() => setFilters({
            ...filters,
            entity: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none" data-id="element-19226">
                  <XIcon className="h-3 w-3" data-id="element-19227" />
                </button>
              </span>}
            {filters.site && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800" data-id="element-19228">
                Site: {filters.site}
                <button type="button" onClick={() => setFilters({
            ...filters,
            site: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none" data-id="element-19229">
                  <XIcon className="h-3 w-3" data-id="element-19230" />
                </button>
              </span>}
            {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-19231">
                Date:{' '}
                {filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString(locale) : 'Any'}
                {filters.dateTo ? ` to ${new Date(filters.dateTo).toLocaleDateString(locale)}` : ''}
                <button type="button" onClick={() => setFilters({
            ...filters,
            dateFrom: '',
            dateTo: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none" data-id="element-19232">
                  <XIcon className="h-3 w-3" data-id="element-19233" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19234">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-3" data-id="element-19235">
            <div data-id="element-19236">
              <label htmlFor="moduleType" className="block text-sm font-medium text-gray-700" data-id="element-19237">
                Module Type
              </label>
              <select id="moduleType" name="moduleType" value={filters.moduleType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19238">
                <option value="" data-id="element-19239">All Modules</option>
                {uniqueModuleTypes.map(moduleType => <option key={moduleType} value={moduleType} data-id="element-19240">
                    {moduleType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19241">
              <label htmlFor="noteType" className="block text-sm font-medium text-gray-700" data-id="element-19242">
                Note Type
              </label>
              <select id="noteType" name="noteType" value={filters.noteType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19243">
                <option value="" data-id="element-19244">All Types</option>
                {uniqueNoteTypes.map(noteType => <option key={noteType} value={noteType} data-id="element-19245">
                    {noteType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19246">
              <label htmlFor="author" className="block text-sm font-medium text-gray-700" data-id="element-19247">
                Author
              </label>
              <select id="author" name="author" value={filters.author} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19248">
                <option value="" data-id="element-19249">All Authors</option>
                {uniqueAuthors.map(author => <option key={author} value={author} data-id="element-19250">
                    {author}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19251">
              <label htmlFor="visibility" className="block text-sm font-medium text-gray-700" data-id="element-19252">
                Visibility
              </label>
              <select id="visibility" name="visibility" value={filters.visibility} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19253">
                <option value="" data-id="element-19254">All Visibilities</option>
                {uniqueVisibilities.map(visibility => <option key={visibility} value={visibility} data-id="element-19255">
                    {visibility}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19256">
              <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-19257">
                Entity
              </label>
              <select id="entity" name="entity" value={filters.entity} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19258">
                <option value="" data-id="element-19259">All Entities</option>
                {uniqueEntities.map(entity => <option key={entity} value={entity} data-id="element-19260">
                    {entity}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19261">
              <label htmlFor="site" className="block text-sm font-medium text-gray-700" data-id="element-19262">
                Site
              </label>
              <select id="site" name="site" value={filters.site} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19263">
                <option value="" data-id="element-19264">All Sites</option>
                {uniqueSites.map(site => <option key={site} value={site} data-id="element-19265">
                    {site}
                  </option>)}
              </select>
            </div>
            <div data-id="element-19266">
              <label htmlFor="dateFrom" className="block text-sm font-medium text-gray-700" data-id="element-19267">
                From Date
              </label>
              <input type="date" id="dateFrom" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19268" />
            </div>
            <div data-id="element-19269">
              <label htmlFor="dateTo" className="block text-sm font-medium text-gray-700" data-id="element-19270">
                To Date
              </label>
              <input type="date" id="dateTo" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19271" />
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19272">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-19273">
          <thead className="bg-gray-50" data-id="element-19274">
            <tr data-id="element-19275">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('timestamp')} data-id="element-19276">
                <div className="flex items-center space-x-1" data-id="element-19277">
                  <span data-id="element-19278">Timestamp</span>
                  {sortField === 'timestamp' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-19279" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-19280" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('module')} data-id="element-19281">
                <div className="flex items-center space-x-1" data-id="element-19282">
                  <span data-id="element-19283">Module</span>
                  {sortField === 'module' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-19284" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-19285" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('noteType')} data-id="element-19286">
                <div className="flex items-center space-x-1" data-id="element-19287">
                  <span data-id="element-19288">Note Type</span>
                  {sortField === 'noteType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-19289" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-19290" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('author')} data-id="element-19291">
                <div className="flex items-center space-x-1" data-id="element-19292">
                  <span data-id="element-19293">Author</span>
                  {sortField === 'author' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-19294" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-19295" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('visibility')} data-id="element-19296">
                <div className="flex items-center space-x-1" data-id="element-19297">
                  <span data-id="element-19298">Visibility</span>
                  {sortField === 'visibility' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-19299" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-19300" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19301">
                Content
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19302">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-19303">
            {sortedNotes.length === 0 ? <tr data-id="element-19304">
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-19305">
                  No notes found matching the current filters.
                </td>
              </tr> : sortedNotes.map(note => <tr key={note.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onViewNote(note)} data-id="element-19306">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19307">
                    {formatTimestamp(note.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-19308">
                    <div className="text-sm font-medium text-indigo-600" data-id="element-19309">
                      {note.moduleType} #{note.moduleId}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-19310">
                      {note.moduleName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19311">
                    {note.noteTypeName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19312">
                    {note.authorName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-19313">
                    {renderVisibilityBadge(note.visibility)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-19314">
                    <div className="max-w-xs" data-id="element-19315">
                      <p className="truncate" data-id="element-19316">{truncateBody(note.body)}</p>
                      {note.tags && note.tags.length > 0 && <div className="mt-1 flex flex-wrap gap-1" data-id="element-19317">
                          {note.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19318">
                              <TagIcon className="h-3 w-3 mr-1" data-id="element-19319" />
                              {tag}
                            </span>)}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-19320">
                    <div className="flex justify-end items-center" data-id="element-19321">
                      {note.hasAttachment && <PaperclipIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-19322" />}
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-19323" />
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}